import { LockFilled } from '@ant-design/icons';
import { Button, Collapse, Result, Space, Typography } from 'antd';
import React from 'react';
import { Link, isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { describeRouteError } from './Error.utils';

function ErrorPageSubtitle({ message }: { message: string }) {
  return (
    <>
      <Typography>{message}</Typography>
      <Link to="/">
        <Button style={{ marginTop: 20 }}>Home Page</Button>
      </Link>
    </>
  );
}

export function Page401() {
  return (
    <Result
      icon={<LockFilled style={{ color: '#ff603b' }} />}
      title={'Authentication Error'}
      subTitle={<ErrorPageSubtitle message={'Your credentials are invalid. Please try to log in again.'} />}
    />
  );
}

export function Page403() {
  return (
    <Result
      status="403"
      title={'Access Denied'}
      subTitle={<ErrorPageSubtitle message={'You are not authorized to access this content.'} />}
    />
  );
}

export function Page404() {
  return (
    <Result
      status="404"
      title={'Page Not Found'}
      subTitle={<ErrorPageSubtitle message={'Sorry, the page you visited does not exist.'} />}
    />
  );
}

export function PageError() {
  return (
    <Result
      status="500"
      title={'Error'}
      subTitle={
        <ErrorPageSubtitle
          message={
            'Something went wrong. Please try again later. If the problem persists, please contact the administrator.'
          }
        />
      }
    />
  );
}

export interface PageRouteErrorProps {
  /**
   * Whether to render the collapsible "Technical details" panel containing the
   * raw HTTP response body, request URL/method, and (for non-HTTP errors) the
   * JS error stack. Useful for support tickets but potentially sensitive in
   * production. Resolved from `DecafWebappProps.errorDetails` upstream.
   */
  showDetails?: boolean;
}

export function PageRouteError({ showDetails = true }: PageRouteErrorProps = {}) {
  const error = useRouteError();
  const navigate = useNavigate();

  // Preserve the existing dedicated pages for the canonical auth/not-found
  // statuses — they have bespoke copy and visuals consumers may already rely
  // on. Only Response throws can hit this branch; axios/Apollo flow through
  // describeRouteError below.
  if (isRouteErrorResponse(error)) {
    if (error.status === 401) return <Page401 />;
    if (error.status === 403) return <Page403 />;
    if (error.status === 404) return <Page404 />;
  }

  const { status, title, subTitle, details } = describeRouteError(error);

  return (
    <Result
      status={status}
      title={title}
      subTitle={subTitle}
      extra={
        <Space direction="vertical" align="center" size="middle" style={{ width: '100%' }}>
          <Space>
            <Button type="primary" onClick={() => navigate(0)}>
              Retry
            </Button>
            <Button onClick={() => navigate('/')}>Go home</Button>
          </Space>
          {showDetails && details && (
            <Collapse
              ghost
              size="small"
              items={[
                {
                  key: 'details',
                  label: 'Technical details',
                  children: (
                    <Typography.Paragraph>
                      <pre
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          textAlign: 'left',
                          margin: 0,
                          maxHeight: 300,
                          overflow: 'auto',
                        }}
                      >
                        {details}
                      </pre>
                    </Typography.Paragraph>
                  ),
                },
              ]}
              style={{ maxWidth: 720 }}
            />
          )}
        </Space>
      }
    />
  );
}
